import React, { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { getCheckoutStatus, getPaymentIntentStatus } from "../lib/api";
import { useCart } from "../context/CartContext";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 12;

const CheckoutSuccess = () => {
  const location = useLocation();
  const { clearCart } = useCart();
  const [state, setState] = useState({
    phase: "polling", // polling | paid | failed | expired | error
    data: null,
    attempts: 0,
  });
  const clearedRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // Payment Element flow uses `payment_intent`; legacy hosted Checkout uses `session_id`
    const paymentIntentId =
      params.get("payment_intent") || params.get("payment_intent_id");
    const sessionId = params.get("session_id");
    if (!paymentIntentId && !sessionId) {
      setState({ phase: "error", data: null, attempts: 0 });
      return;
    }

    let cancelled = false;

    const fetchStatus = () =>
      paymentIntentId
        ? getPaymentIntentStatus(paymentIntentId)
        : getCheckoutStatus(sessionId);

    const poll = async (attempt) => {
      try {
        const res = await fetchStatus();
        if (cancelled) return;
        if (res.payment_status === "paid") {
          if (!clearedRef.current) {
            clearedRef.current = true;
            clearCart();
          }
          setState({ phase: "paid", data: res, attempts: attempt });
          return;
        }
        if (res.status === "expired") {
          setState({ phase: "expired", data: res, attempts: attempt });
          return;
        }
        if (attempt >= MAX_ATTEMPTS) {
          setState({ phase: "error", data: res, attempts: attempt });
          return;
        }
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
      } catch (e) {
        if (cancelled) return;
        setState({ phase: "error", data: null, attempts: attempt });
      }
    };

    poll(1);
    return () => {
      cancelled = true;
    };
  }, [location.search, clearCart]);

  const render = () => {
    if (state.phase === "polling") {
      return (
        <>
          <Loader2 className="h-12 w-12 text-[#D10000] animate-spin mb-6" />
          <div className="font-display text-4xl uppercase tracking-tight">
            Confirming…
          </div>
          <p className="mt-4 text-white/60 max-w-md">
            Verifying your payment with Stripe. This usually takes a few
            seconds.
          </p>
        </>
      );
    }
    if (state.phase === "paid") {
      return (
        <>
          <CheckCircle2 className="h-14 w-14 text-[#D10000] mb-6" />
          <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-3">
            ORDER CONFIRMED
          </div>
          <h1
            className="font-display uppercase leading-[0.9] tracking-tight"
            style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}
          >
            You're in.
          </h1>
          <p className="mt-6 text-white/70 max-w-md">
            Your Primenax order is on its way. A confirmation email has been
            sent. Get ready to reclaim your prime.
          </p>
          {state.data?.amount != null && (
            <div className="mt-8 font-display text-2xl tracking-wider">
              Total charged: ${Number(state.data.amount).toFixed(2)}{" "}
              {String(state.data.currency || "USD").toUpperCase()}
            </div>
          )}
          <Link
            to="/shop"
            data-testid="success-shop-cta"
            className="mt-10 inline-flex items-center gap-3 bg-[#D10000] hover:bg-[#A30000] text-white font-display text-lg tracking-widest uppercase px-8 py-4 transition-colors"
          >
            Continue Shopping
            <ArrowRight className="h-4 w-4" />
          </Link>
        </>
      );
    }
    return (
      <>
        <XCircle className="h-14 w-14 text-white/60 mb-6" />
        <h1
          className="font-display uppercase leading-[0.9]"
          style={{ fontSize: "clamp(2rem, 5vw, 4rem)" }}
        >
          Payment status unclear
        </h1>
        <p className="mt-6 text-white/65 max-w-md">
          We couldn't confirm payment yet. If you were charged, you'll receive
          an email shortly. Otherwise, please try again.
        </p>
        <div className="mt-8 flex gap-3">
          <Link
            to="/shop"
            className="border border-white/20 hover:border-white px-6 py-3 font-display tracking-widest uppercase text-sm"
          >
            Back to Shop
          </Link>
          <Link
            to="/contact"
            className="bg-[#D10000] hover:bg-[#A30000] px-6 py-3 font-display tracking-widest uppercase text-sm"
          >
            Contact Support
          </Link>
        </div>
      </>
    );
  };

  return (
    <div className="bg-[#050505] text-white min-h-[80vh] flex items-center">
      <div
        className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 flex flex-col items-start text-left"
        data-testid="checkout-success-page"
      >
        {render()}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
